/**
 * Founding a crew.
 *
 * The level gate is the point of this screen, so it is said up front rather
 * than discovered by pressing a button and being refused. Somebody who cannot
 * make one yet is told how far off they are; somebody who is already in one is
 * told that instead of being offered a form that cannot succeed.
 *
 * Every rule shown here comes from the server - see /api/crews/rules. The tag
 * check in particular is duplicated on purpose: the client refuses early so
 * typing feels honest, and the server refuses again because the client is not
 * the only way in.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSession } from '@/stores/session';
import { openCrew } from './Crew';
import { Button, Field, Input, Modal, Textarea, useToast } from './ui';

type Rules = {
  createLevel: number;
  tagMin: number;
  tagMax: number;
  maxMembers: number;
  /** While crews are new, only staff may found one. */
  staffOnly?: boolean;
};

export function CreateCrew() {
  const me = useSession((s) => s.me);
  const [open, setOpen] = useState(false);
  const rules = useQuery({
    queryKey: ['crew-rules'],
    queryFn: () => api.get<Rules>('/api/crews/rules'),
    staleTime: 10 * 60_000
  });

  if (!me) return null;

  /* Already in one: the button would only ever refuse. */
  if (me.crew) return null;

  /* Staff-only while crews settle; the level gate is still here for when
     that opens up again. */
  const staffOnly = rules.data?.staffOnly ?? true;
  const need = rules.data?.createLevel ?? 10;
  const level = me.level ?? 1;
  const short = staffOnly ? me.role !== 'admin' : level < need;
  const why = staffOnly ? 'Staff make crews for now' : 'Level ' + need + ' to start one';

  return (
    <>
      <button
        type="button"
        onClick={() => !short && setOpen(true)}
        disabled={short}
        title={short ? why : 'Start a crew'}
        className={
          short
            ? 'cursor-not-allowed text-[12px] font-semibold text-ghost'
            : 'text-[12.5px] font-semibold text-accent-hi hover:underline'
        }
      >
        {short ? why : 'Start a crew'}
      </button>

      {open ? <CreateDialog rules={rules.data} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

/**
 * The dialog on its own.
 *
 * The crew screen opens this straight from its "Start a crew" card, which
 * has already said what a crew is - it does not want the small inline button
 * and its level-gate text a second time.
 */
export function CreateCrewPanel({ onClose }: { onClose: () => void }) {
  const rules = useQuery({
    queryKey: ['crew-rules'],
    queryFn: () => api.get<Rules>('/api/crews/rules'),
    staleTime: 10 * 60_000
  });
  return <CreateDialog rules={rules.data} onClose={onClose} />;
}

function CreateDialog({ rules, onClose }: { rules?: Rules; onClose: () => void }) {
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const queryClient = useQueryClient();

  const min = rules?.tagMin ?? 3;
  const max = rules?.tagMax ?? 5;

  /* Uppercase as they type, and refuse anything that is not a letter or a
     digit - the tag is stored upper case, so showing it any other way while
     typing is a small lie about what they are claiming. */
  const setTagClean = (v: string) =>
    setTag(v.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, max));

  const tagOk = tag.length >= min && tag.length <= max;

  async function create() {
    setBusy(true);
    try {
      const res = await api.post<{ crew: { tag: string } }>('/api/crews', { name, tag, description });
      void queryClient.invalidateQueries({ queryKey: ['crews'] });
      void queryClient.invalidateQueries({ queryKey: ['user'] });
      await useSession.getState().refresh();
      toast('Crew founded', 'good');
      onClose();
      openCrew(res.crew.tag);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Start a crew"
      subtitle="The tag follows your name everywhere. Pick one you will still like."
      onClose={onClose}
      footer={
        <>
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button kind="primary" disabled={busy || !tagOk || name.trim().length < 2} onClick={() => void create()}>
            {busy ? 'Founding…' : 'Found crew'}
          </Button>
        </>
      }
    >
      <Field label="Tag" hint={min + '–' + max + ' characters, letters and numbers. Cannot be changed later.'}>
        <Input
          value={tag}
          onChange={(e) => setTagClean(e.target.value)}
          placeholder="OWL"
          spellCheck={false}
          className="font-mono tracking-[.18em] uppercase"
        />
      </Field>

      <Field label="Name">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Night Owls"
          maxLength={32}
        />
      </Field>

      <Field label="About" hint={description.length + ' / 300'}>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          maxLength={300}
          placeholder="Who is this crew for?"
        />
      </Field>
    </Modal>
  );
}
